import type { CurrentUserDto } from '@obralink/shared';
import { CATEGORIAS, type Pregunta } from './preguntas';

/**
 * Qué ayuda corresponde a cada pantalla.
 *
 * El botón flotante de ayuda enseña las guías de la zona donde se está. La
 * lista de cada zona son los identificadores de `preguntas.ts`, en el orden en
 * que se muestran. Se elige el prefijo MÁS LARGO que coincida con la ruta
 * (`/obras/abc` cae en `/obras`), y si ninguno coincide se ofrece lo básico.
 */
interface Zona {
  prefijo: string;
  titulo: string;
  ids: string[];
}

const BASICAS = ['buscador', 'usar-en-el-movil', 'tema-oscuro', 'repetir-tutorial'];

const ZONAS: Zona[] = [
  {
    prefijo: '/panel',
    titulo: 'el panel',
    ids: ['buscador', 'espacio', 'repetir-tutorial', 'usar-en-el-movil'],
  },
  {
    prefijo: '/obras',
    titulo: 'las obras',
    ids: [
      'crear-obra',
      'encontrar-obra',
      'anadir-participantes',
      'historial-obra',
      'subir-archivos',
      'crear-carpeta',
      'no-veo-obra',
    ],
  },
  {
    prefijo: '/documentos',
    titulo: 'los documentos',
    ids: [
      'archivo-general',
      'subir-archivos',
      'archivo-repetido',
      'crear-carpeta',
      'mover-copiar',
      'versiones',
      'compartir-fuera',
      'bloquear',
      'recuperar-papelera',
      'subida-falla',
    ],
  },
  { prefijo: '/papelera', titulo: 'la papelera', ids: ['recuperar-papelera'] },
  { prefijo: '/favoritos', titulo: 'los favoritos', ids: ['favoritos-etiquetas', 'buscador'] },
  { prefijo: '/buscar', titulo: 'la búsqueda', ids: ['buscador', 'subida-falla'] },
  {
    prefijo: '/incidencias',
    titulo: 'las incidencias',
    ids: ['abrir-incidencia', 'sin-cobertura', 'subir-fotos'],
  },
  { prefijo: '/fotos', titulo: 'las fotografías', ids: ['subir-fotos', 'comparar-fotos'] },
  { prefijo: '/tareas', titulo: 'las tareas', ids: ['tareas'] },
  { prefijo: '/mis-tareas', titulo: 'tus tareas', ids: ['tareas'] },
  { prefijo: '/calendario', titulo: 'el calendario', ids: ['tareas'] },
  {
    prefijo: '/usuarios',
    titulo: 'los usuarios',
    ids: ['invitar', 'roles', 'cambiar-rol', 'segundo-factor'],
  },
  { prefijo: '/auditoria', titulo: 'la auditoría', ids: ['auditoria'] },
];

const TODAS = new Map(CATEGORIAS.flatMap((c) => c.preguntas).map((p) => [p.id, p] as const));

/** ¿Debe ver esta persona esta guía? Mismo criterio que el menú: lo que no puede hacer, no se le enseña. */
export function puedeVerGuia(p: Pregunta, user: Pick<CurrentUserDto, 'permissions' | 'roleCode'>) {
  return (
    (!p.permiso || user.permissions.includes(p.permiso)) &&
    (!p.soloAdmin || user.roleCode === 'ADMIN')
  );
}

export function ayudaDeLaZona(
  pathname: string,
  user: Pick<CurrentUserDto, 'permissions' | 'roleCode'>,
): { titulo: string | null; guias: Pregunta[] } {
  const zona = ZONAS.filter(
    (z) => pathname === z.prefijo || pathname.startsWith(`${z.prefijo}/`),
  ).sort((a, b) => b.prefijo.length - a.prefijo.length)[0];

  const ids = zona?.ids ?? BASICAS;
  const guias = ids
    .map((id) => TODAS.get(id))
    .filter((p): p is Pregunta => !!p && puedeVerGuia(p, user));

  return { titulo: zona?.titulo ?? null, guias };
}
